import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'

export const ActionTagExtension = Extension.create({
    name: 'actionTag',

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('actionTag'),
                appendTransaction: (transactions, oldState, newState) => {
                    // Only process if there were actual changes to the document
                    const docChanged = transactions.some(transaction => transaction.docChanged)
                    if (!docChanged) return null

                    // Get the last transaction
                    const lastTransaction = transactions[transactions.length - 1]
                    if (!lastTransaction) return null

                    // Create a transaction for our formatting changes
                    let tr = null

                    // Process action tags by walking through text nodes directly
                    const processedRanges = new Set()

                    newState.doc.descendants((node, pos) => {
                        if (!node.isText || !node.text) return

                        const actionTagRegex = /\B!\w+/g
                        let match

                        while ((match = actionTagRegex.exec(node.text)) !== null) {
                            const start = pos + match.index
                            const end = start + match[0].length

                            // Skip if we've already processed this range
                            const rangeKey = `${start}-${end}`
                            if (processedRanges.has(rangeKey)) continue
                            processedRanges.add(rangeKey)

                            // Only process if the match has at least one character after !
                            if (match[0].length > 1) {
                                try {
                                    // Check if this range is already bold
                                    const $from = newState.doc.resolve(start)
                                    const marks = $from.marks()
                                    const alreadyBold = marks.some(
                                        mark => mark.type.name === 'bold',
                                    )

                                    if (!alreadyBold) {
                                        if (!tr) {
                                            tr = newState.tr
                                        }
                                        const boldMark = newState.schema.marks.bold.create()
                                        tr.addMark(start, end, boldMark)
                                    }
                                } catch (e) {
                                    // Skip if there's an error applying the mark
                                    console.debug('Error applying bold to action tag:', e)
                                }
                            }
                        }
                    })

                    // Also check for action tags that should no longer be bold
                    // (e.g., if user typed a non-word character after the tag)
                    newState.doc.descendants((node, pos) => {
                        if (node.isText && node.marks.some(mark => mark.type.name === 'bold')) {
                            const text = node.text
                            if (!text) return

                            // Check if this bold text is still a valid action tag
                            const actionTagPattern = /^!\w+$/
                            if (!actionTagPattern.test(text)) {
                                // Check if it's a partial action tag that ended with non-word char
                                const partialPattern = /^!\w+/
                                const match = text.match(partialPattern)
                                if (match && match[0].length < text.length) {
                                    // Remove bold from the non-matching part
                                    if (!tr) {
                                        tr = newState.tr
                                    }
                                    const matchEnd = pos + match[0].length
                                    const textEnd = pos + text.length

                                    // Find the bold mark to remove
                                    const boldMark = node.marks.find(
                                        mark => mark.type.name === 'bold',
                                    )
                                    if (boldMark) {
                                        tr.removeMark(matchEnd, textEnd, boldMark)
                                    }
                                }
                            }
                        }
                    })

                    return tr
                },
            }),
        ]
    },
})

export default ActionTagExtension
